import {
  toWSDateString,
  WSTableCell,
  WSTableColumn,
  WSTableColumnSortDirection
} from "@wingspanhq/fe-component-library";
import { formatMoney } from "@wingspanhq/utils/dist/formatMoney";
import { PayableRow, PayableStatus } from "@wingspanhq/wingspan-api-sdk";
import React from "react";
import { selectorSearchRowName } from "../../../../shared/selectors/selectorSearchRowName";
import { selectorSearchRowSecondaryName } from "../../../../shared/selectors/selectorSearchRowSecondaryName";
import { SortField } from "./filters/types";
import { getPayableStatusCellProps } from "./getStatusColumn";

export type ColumnKey =
  | "payee"
  | "payableNumber"
  | "amount"
  | "dueDate"
  | "openedAt"
  | "paidAt"
  | "status";

export const DEFAULT_VISIBLE_COLUMNS: Record<ColumnKey, boolean> = {
  payee: true,
  payableNumber: false,
  amount: true,
  dueDate: false,
  openedAt: false,
  paidAt: false,
  status: true
};

type GetColumnsOptions = {
  visibleColumns?: Partial<Record<ColumnKey, boolean>>;
  sortField?: SortField;
  sortDirection?: WSTableColumnSortDirection;
  // Customizable label for the paid-date column (terminology.sendPaymentsPaidAt).
  paidAtLabel?: string;
  onSortChange?: (
    field: SortField,
    direction: WSTableColumnSortDirection
  ) => void;
};

const buildSortHandler = (field: SortField, options: GetColumnsOptions) => {
  if (!options.onSortChange) {
    return undefined;
  }
  return (direction: WSTableColumnSortDirection) =>
    options.onSortChange!(field, direction);
};

const sortDirectionFor = (
  field: SortField,
  options: GetColumnsOptions
): WSTableColumnSortDirection | undefined =>
  options.sortField === field ? options.sortDirection : undefined;

export const getColumns = (
  options: GetColumnsOptions
): WSTableColumn<PayableRow>[] => {
  const visible = { ...DEFAULT_VISIBLE_COLUMNS, ...options.visibleColumns };

  const payeeColumn: WSTableColumn<PayableRow> = {
    config: {
      gridTemplateSizeMax: "2fr",
      header: { text: "Name" }
    },
    renderFunction: item => {
      const name = selectorSearchRowName(item.data);
      return (
        <WSTableCell
          text={name || "Contractor"}
          secondaryText={selectorSearchRowSecondaryName(item.data)}
          truncationOptions={{ text: { allowTwoLines: true } }}
        />
      );
    }
  };

  const payableNumberColumn: WSTableColumn<PayableRow> = {
    config: {
      gridTemplateSizeMax: "1fr",
      hideOnScreens: ["XS", "S"],
      header: { text: "Invoice #" }
    },
    renderFunction: item => (
      <WSTableCell
        text={item.data.invoiceNumber ?? undefined}
        secondaryText={item.data.lineItems?.[0]?.description ?? undefined}
      />
    )
  };

  const amountColumn: WSTableColumn<PayableRow> = {
    config: {
      header: { text: "Amount" }
    },
    renderFunction: item => {
      const payable = item.data;
      if (payable.amount === undefined || !payable.currency) {
        return <WSTableCell text="-" />;
      }
      return (
        <WSTableCell text={formatMoney(payable.amount, payable.currency)} />
      );
    }
  };

  // dueDate is NOT in the sortable list per SortField, so it stays
  // non-sortable here.
  const dueDateColumn: WSTableColumn<PayableRow> = {
    config: {
      gridTemplateSizeMax: "1fr",
      hideOnScreens: ["XS", "S"],
      header: { text: "Due date" }
    },
    renderFunction: item => (
      <WSTableCell text={toWSDateString(item.data.dueDate, "monthDayYear")} />
    )
  };

  const openedAtColumn: WSTableColumn<PayableRow> = {
    config: {
      gridTemplateSizeMax: "1fr",
      hideOnScreens: ["XS", "S"],
      header: { text: "Opened date" }
    },
    // The "Eligibility pending" label belongs to `Pending` payables only —
    // they are the ones genuinely blocked from opening by an unmet
    // requirement. Every other unopened state (Draft above all) has no opened
    // date to show, so it renders the empty-date placeholder, matching legacy.
    renderFunction: item => {
      const { status, events } = item.data;
      return (
        <WSTableCell
          text={
            status === PayableStatus.PENDING
              ? "Eligibility pending"
              : toWSDateString(events?.openedAt, "monthDayYear")
          }
        />
      );
    }
  };

  const paidAtColumn: WSTableColumn<PayableRow> = {
    config: {
      gridTemplateSizeMax: "1fr",
      hideOnScreens: ["XS", "S"],
      header: { text: options.paidAtLabel ?? "Processed (paid)" },
      onColumnSort: buildSortHandler("events.paidAt", options),
      sortDirection: sortDirectionFor("events.paidAt", options)
    },
    renderFunction: item => (
      <WSTableCell
        text={toWSDateString(item.data.events?.paidAt, "monthDayYear")}
      />
    )
  };

  const statusColumn: WSTableColumn<PayableRow> = {
    config: {
      gridTemplateSizeMax: "1.5fr",
      header: { text: "Status" }
    },
    renderFunction: item => (
      <WSTableCell {...getPayableStatusCellProps(item.data)} />
    )
  };

  const all: Array<{ key: ColumnKey; column: WSTableColumn<PayableRow> }> = [
    { key: "payee", column: payeeColumn },
    { key: "payableNumber", column: payableNumberColumn },
    { key: "amount", column: amountColumn },
    { key: "openedAt", column: openedAtColumn },
    { key: "dueDate", column: dueDateColumn },
    { key: "paidAt", column: paidAtColumn },
    { key: "status", column: statusColumn }
  ];

  return all.filter(c => visible[c.key]).map(c => c.column);
};
